import { useTheme } from '@mui/material';
import {
  ConversationParticipantEntity,
  ConversationParticipationMode,
} from '@provision/conversation-types';
import { ComponentProps, memo, useEffect, useMemo, useRef, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { ColoredAvatar, DynamicIcon, PlatformUserAvatar, ShortcutText } from 'src/components';
import { StyledAvatar } from 'src/components/ColoredAvatar/colored-avatar';
import { SelectMenu, SelectMenuOption } from 'src/components/SelectMenu';
import { appShortcuts } from 'src/constants/appShortcuts';
import { SEARCH_DEBOUNCE_DELAY } from 'src/constants/delays';
import { componentsFontSizes } from 'src/constants/layout';
import {
  PreparedConversationParticipantOption,
  useConversationParticipantOptions,
} from 'src/graphql';
import {
  getParticipantCustomIcon,
  useParticipationBadgeMap,
} from 'src/pages/Conversations/Chat/utils';
import useConversationViewsStore from 'src/store/conversation-views';
import { useSelectedConversationRequesterStore } from 'src/store/selected-conversation-requester';
import { useNotifications, useRegisterShortcuts } from 'src/utils/hooks';
import { useDebouncedValue } from 'src/utils/hooks/use-debounced-value';

// Menu order for the participant groups; anything unmapped sorts last.
const GROUP_ORDER = ['Clients', 'Agents', 'RSM', 'Unidentified'];

const AVATAR_SIZE = { width: '1.25rem', height: '1.25rem' };

type BadgeConfig = ComponentProps<typeof ColoredAvatar>['badgeConfig'];

export const RequesterAvatar = ({
  option,
  badgeConfig,
}: {
  option: PreparedConversationParticipantOption | null;
  badgeConfig?: BadgeConfig;
}) => {
  const theme = useTheme();
  const customIconName = getParticipantCustomIcon(option?.participantEntity) ?? '';

  if (customIconName) {
    return (
      <StyledAvatar sx={AVATAR_SIZE} backgroundColor={theme.palette.custom.OV.sys.primary}>
        <DynamicIcon name={customIconName} sx={{ width: 'unset', height: 'unset' }} />
      </StyledAvatar>
    );
  }
  if (!option?.label) {
    return (
      <DynamicIcon
        name="user"
        library="lucide"
        sx={{ color: (theme) => theme.palette.custom.OV.sys.onSurfaceVariant }}
      />
    );
  }

  const sx = {
    ...AVATAR_SIZE,
    fontSize: theme.utils.important(componentsFontSizes.coloredAvatar),
  };
  return option.participantEntity === ConversationParticipantEntity.PlatformUser ? (
    <PlatformUserAvatar
      partnerId={option.entityPartnerId}
      avatarFileKey={option.avatarFileKey}
      name={option.label}
      badgeConfig={badgeConfig}
      sx={sx}
    />
  ) : (
    <ColoredAvatar name={option.label} badgeConfig={badgeConfig} sx={sx} />
  );
};

const ConversationRequesterToggleComponent = ({
  name,
  partnerId,
  conversationId,
  disabled,
}: {
  name: string;
  partnerId: number;
  conversationId: string;
  disabled?: boolean;
}) => {
  const { control } = useFormContext();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const { showInfoAlert } = useNotifications();
  const participationBadgeMap = useParticipationBadgeMap();
  const setAssociatedAddressesPlaceholders = useConversationViewsStore(
    (state) => state.setAssociatedAddressesPlaceholders,
  );
  const setPartnerSelectedConversationRequester = useSelectedConversationRequesterStore(
    (state) => state.setPartnerSelectedConversationRequester,
  );

  const { getConversationParticipantOptions, conversationParticipantOptions, loading } =
    useConversationParticipantOptions();

  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search, SEARCH_DEBOUNCE_DELAY);
  // `useConversationParticipantOptions` falls back to Apollo's previousData so results
  // stay put while the next query loads. That means the visible rows can belong to an
  // earlier query, so track which one they came from and hide them until the query we
  // are actually asking for lands. Comparing against the search text (rather than
  // remembering the last non-empty one) survives close/reopen, where the box resets to
  // "" with no fetch in flight.
  const requestedSearchRef = useRef<string | null>(null);
  const [fetchedSearch, setFetchedSearch] = useState<string | null>(null);

  useRegisterShortcuts(
    useMemo(
      () => ({
        ...appShortcuts.changeRequester,
        callback: () => {
          // Bail when the trigger has focus (SelectMenu handles the key itself)
          // and while the menu is open, so clicking the trigger below can never
          // toggle an open menu shut.
          if (
            disabled ||
            document.activeElement === triggerRef.current ||
            triggerRef.current?.getAttribute('aria-expanded') === 'true'
          ) {
            return false;
          }
          triggerRef.current?.focus();
          triggerRef.current?.click();
          return false;
        },
      }),
      [disabled],
    ),
  );

  // When search is cleared, fetch empty immediately — don't wait out the debounce,
  // or reopen would request the previous query while the input already shows "".
  const searchText = search === '' ? '' : debouncedSearch;

  // Fetch once the menu opens, then on every search. `allowEmpty` because the
  // groups have to be browsable before the user types anything.
  useEffect(() => {
    if (!open) {
      return;
    }
    requestedSearchRef.current = searchText;
    getConversationParticipantOptions({ partnerId, searchText }, true);
  }, [open, searchText, partnerId, getConversationParticipantOptions]);

  // A fetch settling means the visible rows now belong to whatever it requested.
  useEffect(() => {
    if (!loading && requestedSearchRef.current !== null) {
      setFetchedSearch(requestedSearchRef.current);
    }
  }, [loading]);

  // Sorted by group so `sectioned` renders one heading per category.
  const options = useMemo<SelectMenuOption[]>(() => {
    // Rows from a different query than the current one are stale — drop them rather
    // than showing the old hits under the new (often empty) search box.
    if (fetchedSearch !== searchText) {
      return [];
    }
    return [...conversationParticipantOptions]
      .sort((a, b) => GROUP_ORDER.indexOf(a.group) - GROUP_ORDER.indexOf(b.group))
      .map((participant) => ({
        value: participant.id,
        title: participant.label,
        group: participant.group,
        hint:
          participant.entityPartnerId !== undefined &&
          participant.entityPartnerId !== null &&
          participant.entityPartnerId !== partnerId
            ? `Workspace: ${participant.entityPartnerId}`
            : undefined,
        description:
          participant.email ||
          (participant.participantEntity === ConversationParticipantEntity.Client
            ? 'No email address on client record'
            : undefined),
        icon: <RequesterAvatar option={participant} />,
        keywords: [participant.email, participant.phoneNumber].filter(Boolean),
      }));
  }, [conversationParticipantOptions, fetchedSearch, searchText, partnerId]);

  return (
    <Controller
      name={name}
      control={control}
      render={({ field: { value, onChange } }) => {
        const current = value as PreparedConversationParticipantOption | null;
        const badgeConfig =
          current?.participantEntity === ConversationParticipantEntity.PlatformUser
            ? participationBadgeMap[ConversationParticipationMode.Assist]
            : undefined;

        return (
          <SelectMenu
            ref={triggerRef}
            label="Requester"
            options={options}
            value={current?.id ?? null}
            onChange={(selectedId) => {
              const selected = conversationParticipantOptions.find(
                (participant) => participant.id === selectedId,
              );
              if (!selected) {
                return;
              }
              // Address placeholders belonged to the previous requester.
              if (selected.id !== current?.id) {
                setAssociatedAddressesPlaceholders({ [conversationId]: [] });
              }
              setPartnerSelectedConversationRequester(selected);
              onChange(selected);

              if (
                selected.participantEntity === ConversationParticipantEntity.Client &&
                !selected.email
              ) {
                showInfoAlert(
                  "This requester's client record has no email address, so they won't receive public replies. Add an email to the client record to enable them.",
                );
              }
            }}
            // The selection often isn't in `options` (server-backed, and the list
            // changes with the query), so the trigger renders from the form value.
            triggerLabel={current?.label}
            triggerIcon={<RequesterAvatar option={current} badgeConfig={badgeConfig} />}
            placeholder="Assign"
            searchPlaceholder="Search requesters…"
            searchValue={search}
            onSearchChange={setSearch}
            loading={loading}
            // Controlled search means the reset is ours. Clear on close; `searchText`
            // treats "" as immediate so reopen fetches empty without debounce lag.
            onOpenChange={(next) => {
              setOpen(next);
              if (!next) {
                setSearch('');
              }
            }}
            hotkey={appShortcuts.changeRequester.command}
            variant="inline"
            sectioned
            plainOptionIcons
            disabled={disabled}
            menuWidth="21.875rem"
            sx={{ width: '100%' }}
            tooltip={
              <ShortcutText
                label={current?.label ?? appShortcuts.changeRequester.description}
                shortcut={appShortcuts.changeRequester.command}
                size="small"
              />
            }
          />
        );
      }}
    />
  );
};

export const ConversationRequesterToggle = memo(ConversationRequesterToggleComponent);
